import classNames from 'classnames';
import React from 'react';

import Result from './Result';
import { FuelSavingsCalculatorFragmentFragment } from '../../__generated__/graphql';
import Button from '../../components/Button';
import Card from '../../components/Card';
import Input from '../../components/Input';
import RawHtml from '../../components/RawHtml';
import { Heading3 } from '../../components/Text';
import Section from '../../layout/Section';
import SectionTitle from '../../layout/Section/SectionTitle';
import { useSubmitFuelSavingsCalculator } from '../../services/marketo/api/hooks';
import { ContentBlock } from '../types';

import style from './fuel-saving-calculator.module.css';

interface SavingsFormData {
  annualSavings: number;
  monthlySavings: number;
  totalCost: number;
  weeklySavings: number;
}

const emptyFormData: SavingsFormData = {
  annualSavings: 0,
  monthlySavings: 0,
  totalCost: 0,
  weeklySavings: 0,
};

const formNames = {
  email: 'email',
  milesPerGallon: 'milesPerGallon',
  milesPerWeek: 'milesPerWeek',
  numberOfTrucks: 'numberOfTrucks',
  pricePerGallon: 'pricePerGallon',
} as const;

const defaultMultiplier = 48;

const roundToTwoDecimalPlaces = (value: number): number => Number((Math.round(value * 100) / 100).toFixed(2));

const resultsCardId = 'fuel-calculator-results';

const FuelSavingsCalculator: ContentBlock<FuelSavingsCalculatorFragmentFragment> = ({ fragment }) => {
  const [savingsFormData, setSavingsFormData] = React.useState<SavingsFormData>(emptyFormData);

  const contentBlockProps = fragment.fuelSavingsCalculator;

  const {
    isPending,
    mutate: submitFuelSavingsCalculator,
  } = useSubmitFuelSavingsCalculator();

  if (!contentBlockProps) {
    return null;
  }

  const handleFormSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    const formData = new FormData(evt.currentTarget);

    const numberOfTrucks = Number(formData.get(formNames.numberOfTrucks));
    const milesPerWeek = Number(formData.get(formNames.milesPerWeek));
    const milesPerGallon = Number(formData.get(formNames.milesPerGallon));
    const pricePerGallon = Number(formData.get(formNames.pricePerGallon));
    const weeklyFuelAmount = (numberOfTrucks * milesPerWeek) / (milesPerGallon || 1);
    const totalCost = weeklyFuelAmount * pricePerGallon;

    const ourPricePerGallon = pricePerGallon - ((contentBlockProps.multiplier ?? defaultMultiplier) / 100);
    const ourTotalCost = weeklyFuelAmount * ourPricePerGallon;

    const weeklySavings = totalCost - ourTotalCost;

    const annualSavings = weeklySavings * 52;
    const monthlySavings = weeklySavings * 4;

    setSavingsFormData({
      annualSavings,
      monthlySavings,
      totalCost,
      weeklySavings,
    });

    document.getElementById(resultsCardId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    submitFuelSavingsCalculator({
      formId: Number(contentBlockProps.marketoFormId),
      input: [{
        leadFormFields: {
          email: formData.get(formNames.email) as string,
          fuelCalAnnualSavings: roundToTwoDecimalPlaces(annualSavings),
          fuelCalMilespergallon: milesPerGallon,
          fuelCalMilesperweekpertruck: milesPerWeek,
          fuelCalMonthlySavings: roundToTwoDecimalPlaces(monthlySavings),
          fuelCalNumberoftrucks: numberOfTrucks,
          fuelCalPriceyoupaypergallon: pricePerGallon,
          fuelCalTotalspendperweek: roundToTwoDecimalPlaces(totalCost),
          fuelCalWeeklySavings: roundToTwoDecimalPlaces(weeklySavings),
        },
        visitorData: {
          pageURL: window.location.href,
          queryString: window.location.search,
          userAgentString: window.navigator.userAgent,
        },
      }],
    });
  };

  return (
    <Section
      backgroundColor="white"
      id={contentBlockProps.sectionId}
    >
      {/* TODO: remove this className */}
      <SectionTitle className={style.title}>
        {contentBlockProps.sectionTitle}
      </SectionTitle>

      <div className={style.cardContainer}>
        <Card
          backgroundColor="white"
          border="black"
          className={style.card}
        >
          <Heading3
            className={style.cardTitle}
            fontSize={20}
          >
            Your company info
          </Heading3>

          <form
            className={style.form}
            onSubmit={handleFormSubmit}
          >
            <Input
              inputMode="numeric"
              label="Number of Trucks *"
              min={0}
              name={formNames.numberOfTrucks}
              required
              type="number"
            />
            <Input
              inputMode="decimal"
              label="Miles Per Week Per Truck *"
              min={0}
              name={formNames.milesPerWeek}
              required
              type="number"
            />
            <Input
              inputMode="decimal"
              label="Miles Per Gallon *"
              min={0}
              name={formNames.milesPerGallon}
              required
              step="0.1"
              type="number"
            />
            <Input
              inputMode="decimal"
              label="Price You Pay Per Gallon of Diesel *"
              min={0}
              name={formNames.pricePerGallon}
              required
              step="0.01"
              type="number"
            />
            <Input
              inputMode="email"
              label="Email *"
              name={formNames.email}
              required
              type="email"
            />

            {contentBlockProps.outroCopy && (
              <RawHtml>
                {contentBlockProps.outroCopy}
              </RawHtml>
            )}

            <Button
              buttonColor="black"
              isLoading={isPending}
            >
              Calculate
            </Button>
          </form>
        </Card>

        <Card
          backgroundColor="white"
          border="black"
          className={classNames(style.card, style.resultsCard)}
          id={resultsCardId}
        >
          <Heading3
            className={style.cardTitle}
            fontSize={20}
          >
            Results
          </Heading3>

          <div className={style.results}>
            <Result
              label="Total Spent on Diesel in a Week"
              value={savingsFormData.totalCost}
            />
            <Result
              label="Weekly Savings"
              value={savingsFormData.weeklySavings}
            />
            <Result
              label="Monthly Savings"
              value={savingsFormData.monthlySavings}
            />
            <Result
              label="Annual Savings"
              value={savingsFormData.annualSavings}
            />
          </div>
        </Card>
      </div>
    </Section>
  );
};

export default FuelSavingsCalculator;
